import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { unzoom } from '@/lib/uiScale'
import { cn } from '@/lib/utils'

export interface MenuItem {
  key: string
  label: ReactNode
  onSelect: () => void
  /** Tailwind text colour class, e.g. "text-green-700". */
  className?: string
  /** Renders a ✓ after the label - marks the current state. */
  checked?: boolean
  disabled?: boolean
}

export interface MenuSection {
  /** Small grey caption above the group. */
  label?: ReactNode
  items: MenuItem[]
}

interface Props {
  /** Viewport coordinates of the click that opened the menu. */
  x: number
  y: number
  sections: MenuSection[]
  onClose: () => void
}

const MARGIN = 6

/**
 * Compact text menu anchored to a point, rendered in a portal.
 *
 * The calendar scrolls horizontally, so the menu cannot live inside the grid -
 * `overflow-x-auto` would clip it. Portal + fixed positioning keeps it on top,
 * and it flips when it would leave the viewport.
 */
export default function ContextMenu({ x, y, sections, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: x, top: y, ready: false })

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    // Počíta sa v súradniciach obrazovky (`clientX`), zapisuje v mierke
    // stránky - viď `unzoom`.
    setPos({
      left: unzoom(Math.max(MARGIN, Math.min(x, window.innerWidth - width - MARGIN))),
      top: unzoom(Math.max(MARGIN, y + height + MARGIN > window.innerHeight ? y - height : y)),
      ready: true,
    })
  }, [x, y])

  useEffect(() => {
    const close = () => onClose()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [onClose])

  return createPortal(
    <div
      ref={ref}
      role="menu"
      className={cn(
        'fixed z-[60] min-w-[11rem] rounded border border-slate-300 bg-white py-1.5 shadow-lg',
        !pos.ready && 'invisible',
      )}
      style={{ left: pos.left, top: pos.top }}
    >
      {/* Sections are built with conditional items (read-only users, disabled
          modules), so an empty one is normal - it must not leave a divider
          floating with nothing under it. */}
      {sections
        .filter((section) => section.items.length > 0)
        .map((section, index) => (
        <div key={index}>
          {index > 0 ? <div className="my-1.5 border-t border-slate-200" /> : null}
          {section.label ? (
            <div className="px-4 pb-0.5 pt-0.5 text-[13px] text-slate-400">
              {section.label}
            </div>
          ) : null}
          {section.items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                onClose()
                item.onSelect()
              }}
              className={cn(
                'block w-full px-4 py-[3px] text-left text-[13px] leading-5',
                'hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-40',
                item.className ?? 'text-slate-700',
              )}
            >
              {item.label}
              {item.checked ? <span className="ml-1.5">✓</span> : null}
            </button>
          ))}
        </div>
        ))}
    </div>,
    document.body,
  )
}
